// useOfftake — fetches the Port & Offtake payload for a single selected
// project from GET /api/v1/offtake/at?lat&lng (offtake Phase 1).
//
// Payload (backend/app/api/offtake.py):
//   nearest_port.{name, unlocode, distance_km, alternatives[], source}
//   maritime_activity.{window, by_class[], source}        — real rows from
//     data_layers.port_calls_no (SafeSeaNet via Kystdatahuset), or a
//     {badge, note} object when the harbour has no xref / no rows.
//   nautical_access.{controlling_depth_m, berth_depth_m,
//     depth_sources.{controlling_depth, berth_depth}.{badge, reason},
//     pilotage.{regime, boarding_distance_nm, nearest_boarding_name,
//               cost_nok_per_call}}
//
// Same shape of hook as useGridFeasibility: component-local, cached by
// project id so flipping between sites doesn't refetch. The endpoint is
// unauthenticated and sub-100 ms (all local PostGIS), so no token.

import { useEffect, useMemo, useRef, useState } from 'react';

const API_BASE = import.meta.env?.VITE_API_URL || '';

export function useOfftake(project) {
  const [cache] = useState(() => new Map());  // id → { status, data, error }
  const [tick, setTick] = useState(0);
  const inFlight = useRef(new Set());

  const id = project?.id ?? null;

  const load = (p) => {
    inFlight.current.add(p.id);
    cache.set(p.id, { status: 'loading', data: null, error: null });
    setTick(t => t + 1);
    fetch(`${API_BASE}/api/v1/offtake/at?lat=${p.lat}&lng=${p.lon}`)
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => cache.set(p.id, { status: 'ready', data, error: null }))
      .catch(e => cache.set(p.id, { status: 'error', data: null, error: e.message || 'failed' }))
      .finally(() => { inFlight.current.delete(p.id); setTick(t => t + 1); });
  };

  useEffect(() => {
    if (!project || project.lat == null || project.lon == null) return;
    if (cache.has(project.id) || inFlight.current.has(project.id)) return;
    load(project);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const entry = id != null ? cache.get(id) : null;
  return {
    status: entry?.status || 'idle',
    data:   entry?.data || null,
    error:  entry?.error || null,
    retry:  () => { if (project) { cache.delete(project.id); load(project); } },
    _tick:  tick,
  };
}

// useOfftakeMany — the same fetch for every project on the page, keyed by
// id, so the compare table can read enrichment._offtake rows. Returns a
// map id → payload (null until loaded / on error). Projects outside
// Norway or without coords are skipped.
export function useOfftakeMany(projects) {
  const [cache] = useState(() => new Map());
  const [tick, setTick] = useState(0);
  const inFlight = useRef(new Set());

  const ids = (projects || []).map(p => p.id).join('|');
  useEffect(() => {
    for (const p of projects || []) {
      if (!p || p.lat == null || p.lon == null) continue;
      if ((p.country || 'NO') !== 'NO') continue;
      if (cache.has(p.id) || inFlight.current.has(p.id)) continue;
      inFlight.current.add(p.id);
      fetch(`${API_BASE}/api/v1/offtake/at?lat=${p.lat}&lng=${p.lon}`)
        .then(r => (r.ok ? r.json() : null))
        .then(data => cache.set(p.id, data))
        .catch(() => cache.set(p.id, null))
        .finally(() => { inFlight.current.delete(p.id); setTick(t => t + 1); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => Object.fromEntries(cache), [tick]);
}
